import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { getTodayIST, dateInputToISO } from '../utils/dateUtils';

// Bank Transfer Modal with Subtypes
export const BankTransferModal = ({ isOpen, onClose, onSave, editData }) => {
  const [subtype, setSubtype] = useState(''); // imps, neft, cheque
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(getTodayIST());
  const [note, setNote] = useState('');
  
  // IMPS/NEFT fields
  const [utrRef, setUtrRef] = useState('');
  const [bankName, setBankName] = useState('');
  const [payerAcct, setPayerAcct] = useState('');
  
  // Cheque fields
  const [chequeNo, setChequeNo] = useState('');
  const [chequeBank, setChequeBank] = useState('');
  const [chequeDate, setChequeDate] = useState(getTodayIST());
  const [chequeStatus, setChequeStatus] = useState('Received');

  const resetForm = () => {
    setSubtype('');
    setAmount('');
    setDate(getTodayIST());
    setNote('');
    setUtrRef('');
    setBankName('');
    setPayerAcct('');
    setChequeNo('');
    setChequeBank('');
    setChequeDate(getTodayIST());
    setChequeStatus('Received');
  };

  // Pre-fill if editing
  useEffect(() => {
    if (editData) {
      setAmount(editData.amount?.toString() || '');
      setDate(editData.date || getTodayIST());
      setNote(editData.note || '');
      
      if (editData.mode === 'IMPS') {
        setSubtype('imps');
        setUtrRef(editData.utr_ref || '');
        setBankName(editData.bank_name || '');
        setPayerAcct(editData.payer_acct_last4 || '');
      } else if (editData.mode === 'NEFT/RTGS') {
        setSubtype('neft');
        setUtrRef(editData.utr_ref || '');
        setBankName(editData.bank_name || '');
        setPayerAcct(editData.payer_acct_last4 || '');
      } else if (editData.mode === 'Cheque') {
        setSubtype('cheque');
        setChequeNo(editData.cheque_no || '');
        setChequeBank(editData.cheque_bank || '');
        setChequeDate(editData.cheque_date || getTodayIST());
        setChequeStatus(editData.cheque_status || 'Received');
      }
    } else {
      resetForm();
    }
  }, [editData, isOpen]);

  if (!isOpen) return null;

  const handleSave = () => {
    if (!amount || amount === '0' || !subtype) return;
    
    const amountValue = parseFloat(amount);
    const baseData = {
      amount: Math.abs(amountValue),
      date: dateInputToISO(date),
      note,
      transaction_type: amountValue < 0 ? 'REFUND' : 'PAYMENT',
      direction: amountValue < 0 ? 'OUT' : 'IN',
      timestamp: editData?.timestamp || dateInputToISO(date)
    };

    if (subtype === 'imps' || subtype === 'neft') {
      onSave({
        ...baseData,
        mode: subtype === 'imps' ? 'IMPS' : 'NEFT/RTGS',
        reference: `${subtype.toUpperCase()}: ${utrRef} - ${bankName}`,
        utr_ref: utrRef,
        bank_name: bankName,
        payer_acct_last4: payerAcct
      });
    } else if (subtype === 'cheque') {
      if (!chequeNo || !chequeBank || !chequeDate) return;
      onSave({
        ...baseData,
        mode: 'Cheque',
        reference: `Cheque: ${chequeNo} - ${chequeBank}`,
        cheque_no: chequeNo,
        cheque_bank: chequeBank,
        cheque_date: chequeDate,
        cheque_status: chequeStatus
      });
    }
    
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50" onClick={onClose}>
      <div 
        className="bg-white rounded-t-3xl w-full max-w-md p-6 space-y-4 animate-slide-up max-h-[85vh] overflow-y-auto" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-bold text-gray-800">🏦 Bank Transfer</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3">
          {/* Subtype Selector */}
          {!subtype && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-2">Transfer Type</label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setSubtype('imps')}
                  className="py-3 px-3 rounded-lg bg-amber-100 text-amber-800 font-semibold text-xs hover:bg-amber-200"
                >
                  IMPS
                </button>
                <button
                  type="button"
                  onClick={() => setSubtype('neft')}
                  className="py-3 px-3 rounded-lg bg-amber-100 text-amber-800 font-semibold text-xs hover:bg-amber-200"
                >
                  NEFT/RTGS
                </button>
                <button
                  type="button"
                  onClick={() => setSubtype('cheque')}
                  className="py-3 px-3 rounded-lg bg-amber-100 text-amber-800 font-semibold text-xs hover:bg-amber-200"
                >
                  Cheque
                </button>
              </div>
            </div>
          )}

          {subtype && (
            <>
              <div className="flex items-center justify-between py-2 bg-amber-50 px-3 rounded">
                <span className="text-sm font-semibold text-amber-800">
                  {subtype === 'imps' ? 'IMPS' : subtype === 'neft' ? 'NEFT/RTGS' : 'Cheque'}
                </span>
                {!editData && (
                  <button
                    type="button"
                    onClick={() => setSubtype('')}
                    className="text-xs text-amber-600 underline"
                  >
                    Change
                  </button>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Amount (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  className="input-field w-full"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter amount (use - for refund)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  💡 Tip: Enter negative amount (e.g., -5000) to record a refund
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  className="input-field w-full"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>

              {/* IMPS/NEFT Fields */}
              {(subtype === 'imps' || subtype === 'neft') && (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">UTR/Ref No. (optional)</label>
                    <input
                      type="text"
                      className="input-field w-full"
                      value={utrRef}
                      onChange={(e) => setUtrRef(e.target.value)}
                      placeholder="e.g., HDFC123456789"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Bank Name (optional)</label>
                    <input
                      type="text"
                      className="input-field w-full"
                      value={bankName}
                      onChange={(e) => setBankName(e.target.value)}
                      placeholder="e.g., HDFC Bank"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Payer Acct Last 4 (optional)</label>
                    <input
                      type="text"
                      maxLength="4"
                      className="input-field w-full"
                      value={payerAcct}
                      onChange={(e) => setPayerAcct(e.target.value.replace(/\D/g, ''))}
                      placeholder="1234"
                    />
                  </div>
                </>
              )}

              {/* Cheque Fields */}
              {subtype === 'cheque' && (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Cheque No. *</label>
                    <input
                      type="text"
                      className="input-field w-full"
                      value={chequeNo}
                      onChange={(e) => setChequeNo(e.target.value)}
                      placeholder="e.g., 123456"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Bank *</label>
                    <input
                      type="text"
                      className="input-field w-full"
                      value={chequeBank}
                      onChange={(e) => setChequeBank(e.target.value)}
                      placeholder="e.g., HDFC Bank"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Cheque Date *</label>
                    <input
                      type="date"
                      className="input-field w-full"
                      value={chequeDate}
                      onChange={(e) => setChequeDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Status</label>
                    <div className="flex gap-2">
                      {['Received', 'Cleared', 'Bounced'].map(status => (
                        <button
                          key={status}
                          type="button"
                          onClick={() => setChequeStatus(status)}
                          className={`flex-1 py-2 rounded-lg text-xs font-semibold ${
                            chequeStatus === status
                              ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-white'
                              : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          {status}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Note (optional)</label>
                <input
                  type="text"
                  className="input-field w-full"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Payment note"
                />
              </div>
            </>
          )}
        </div>

        {subtype && (
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!amount || (subtype === 'cheque' && (!chequeNo || !chequeBank))}
              className="flex-1 py-3 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-lg font-semibold disabled:opacity-50"
            >
              {editData ? 'Update' : 'Save'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Old Gold Purchase Modal
export const OldGoldModal = ({ isOpen, onClose, onSave, editData }) => {
  const [items, setItems] = useState([{
    id: Date.now(),
    item_name: '',
    gross_wt: '',
    less_wt: '',
    net_wt: '',
    purity: '',
    fine_wt: '',
    purchase_rate: '',
    line_total: ''
  }]);
  const [date, setDate] = useState(getTodayIST());
  const [note, setNote] = useState('');

  // Pre-fill if editing
  useEffect(() => {
    if (editData && editData.items && editData.items.length > 0) {
      // Load existing items with IDs
      const loadedItems = editData.items.map((item, idx) => ({
        id: Date.now() + idx,
        ...item
      }));
      setItems(loadedItems);
      setDate(editData.date || getTodayIST());
      setNote(editData.note || '');
    } else {
      // Reset to default (including when items array is empty)
      setItems([{
        id: Date.now(),
        item_name: '',
        gross_wt: '',
        less_wt: '',
        net_wt: '',
        purity: '',
        fine_wt: '',
        purchase_rate: '',
        line_total: ''
      }]);
      setDate(editData?.date || getTodayIST());
      setNote(editData?.note || '');
    }
  }, [editData, isOpen]);

  if (!isOpen) return null;

  const addItem = () => {
    setItems([...items, {
      id: Date.now(),
      item_name: '',
      gross_wt: '',
      less_wt: '',
      net_wt: '',
      purity: '',
      fine_wt: '',
      purchase_rate: '',
      line_total: ''
    }]);
  };

  const removeItem = (id) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };

  const updateItem = (id, field, value) => {
    setItems(items.map(item => {
      if (item.id !== id) return item;
      
      const updated = { ...item, [field]: value };
      
      // Auto-calculate Net Wt
      if (field === 'gross_wt' || field === 'less_wt') {
        const gross = parseFloat(updated.gross_wt) || 0;
        const less = parseFloat(updated.less_wt) || 0;
        updated.net_wt = Math.max(0, gross - less).toFixed(3);
      }
      
      // Auto-calculate Fine Wt
      if (field === 'net_wt' || field === 'purity' || (field === 'gross_wt' || field === 'less_wt')) {
        const net = parseFloat(updated.net_wt) || 0;
        const purity = parseFloat(updated.purity) || 0;
        updated.fine_wt = (net * purity / 100).toFixed(3);
      }
      
      // Auto-calculate Line Total
      if (field === 'fine_wt' || field === 'purchase_rate' || field === 'net_wt' || field === 'purity') {
        const fine = parseFloat(updated.fine_wt) || 0;
        const rate = parseFloat(updated.purchase_rate) || 0;
        updated.line_total = (fine * rate).toFixed(2);
      }
      
      return updated;
    }));
  };

  const getTotals = () => {
    const totalFine = items.reduce((sum, item) => sum + (parseFloat(item.fine_wt) || 0), 0);
    const totalAmount = items.reduce((sum, item) => sum + (parseFloat(item.line_total) || 0), 0);
    return { totalFine: totalFine.toFixed(3), totalAmount: totalAmount.toFixed(2) };
  };

  const handleSave = () => {
    const { totalFine, totalAmount } = getTotals();
    if (!totalAmount || parseFloat(totalAmount) === 0) return;
    
    onSave({
      mode: 'Old Gold',
      amount: parseFloat(totalAmount),
      date: dateInputToISO(date),
      note,
      reference: `Old Gold: ${totalFine}g fine weight`,
      fine_grams: parseFloat(totalFine),
      items: items.map(({ id, ...item }) => item),
      timestamp: editData?.timestamp || dateInputToISO(date)
    });
    onClose();
  };

  const { totalFine, totalAmount } = getTotals();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50" onClick={onClose}>
      <div 
        className="bg-white rounded-t-3xl w-full max-w-md p-6 space-y-4 animate-slide-up max-h-[85vh] overflow-y-auto" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-bold text-gray-800">🪙 {editData ? 'Edit Old Gold Purchase' : 'Old Gold Purchase'}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="bg-amber-50 p-3 rounded-lg">
          <div className="flex justify-between text-sm font-semibold">
            <span>Items: {items.length}</span>
            <span>Total Fine: {totalFine}g</span>
            <span className="text-amber-700">₹{totalAmount}</span>
          </div>
        </div>

        <div className="space-y-4">
          {items.map((item, idx) => (
            <div key={item.id} className="border-2 border-amber-200 rounded-lg p-3 space-y-2">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-bold text-amber-700">Item #{idx + 1}</span>
                {items.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeItem(item.id)}
                    className="text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              
              <input
                type="text"
                className="input-field w-full text-sm"
                value={item.item_name}
                onChange={(e) => updateItem(item.id, 'item_name', e.target.value)}
                placeholder="Item name"
              />
              
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs text-gray-600">Gross (g)</label>
                  <input
                    type="number"
                    step="0.001"
                    className="input-field w-full text-sm"
                    value={item.gross_wt}
                    onChange={(e) => updateItem(item.id, 'gross_wt', e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Less (g)</label>
                  <input
                    type="number"
                    step="0.001"
                    className="input-field w-full text-sm"
                    value={item.less_wt}
                    onChange={(e) => updateItem(item.id, 'less_wt', e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Net (g)</label>
                  <input
                    type="number"
                    step="0.001"
                    className="input-field w-full text-sm bg-gray-50"
                    value={item.net_wt}
                    onChange={(e) => updateItem(item.id, 'net_wt', e.target.value)}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-600">Purity %</label>
                  <input
                    type="number"
                    step="0.01"
                    className="input-field w-full text-sm"
                    value={item.purity}
                    onChange={(e) => updateItem(item.id, 'purity', e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Fine (g)</label>
                  <input
                    type="number"
                    step="0.001"
                    className="input-field w-full text-sm bg-gray-50"
                    value={item.fine_wt}
                    onChange={(e) => updateItem(item.id, 'fine_wt', e.target.value)}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-600">Rate (₹/g)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="input-field w-full text-sm"
                    value={item.purchase_rate}
                    onChange={(e) => updateItem(item.id, 'purchase_rate', e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Total (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="input-field w-full text-sm bg-gray-50 font-semibold"
                    value={item.line_total}
                    readOnly
                  />
                </div>
              </div>
            </div>
          ))}
          
          <button
            type="button"
            onClick={addItem}
            className="w-full py-2 border-2 border-dashed border-amber-300 rounded-lg text-amber-600 font-semibold text-sm flex items-center justify-center gap-1"
          >
            <Plus className="w-4 h-4" />
            Add Another Item
          </button>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Date</label>
          <input
            type="date"
            className="input-field w-full"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Note (optional)</label>
          <input
            type="text"
            className="input-field w-full"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Purchase note"
          />
        </div>

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={parseFloat(totalAmount) === 0}
            className="flex-1 py-3 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-lg font-semibold disabled:opacity-50"
          >
            {editData ? 'Update' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Metal (Fine Gold) Modal
export const MetalCreditModal = ({ isOpen, onClose, onSave, editData }) => {
  const [metalType, setMetalType] = useState('Cadbury');
  const [purity, setPurity] = useState('');
  const [netWt, setNetWt] = useState('');
  const [fineWt, setFineWt] = useState('');
  const [reference, setReference] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  // Pre-fill if editing
  useEffect(() => {
    if (editData) {
      setMetalType(editData.metal_type || 'Cadbury');
      setPurity(editData.purity?.toString() || '');
      setNetWt(editData.net_wt?.toString() || '');
      setFineWt(editData.fine_wt?.toString() || '');
      setReference(editData.reference || '');
      setDate(editData.date || new Date().toISOString().split('T')[0]);
    } else {
      setMetalType('Cadbury');
      setPurity('');
      setNetWt('');
      setFineWt('');
      setReference('');
      setDate(new Date().toISOString().split('T')[0]);
    }
  }, [editData, isOpen]);

  // Auto-calculate fine weight
  useEffect(() => {
    if (netWt && purity) {
      const fine = (parseFloat(netWt) * parseFloat(purity) / 100).toFixed(3);
      setFineWt(fine);
    }
  }, [netWt, purity]);

  if (!isOpen) return null;

  const handleSave = () => {
    if (!netWt || !purity || !fineWt) return;
    
    onSave({
      mode: 'Metal Credit',
      metal_type: metalType,
      purity: parseFloat(purity),
      net_wt: parseFloat(netWt),
      fine_wt: parseFloat(fineWt),
      reference: `Metal Credit: ${metalType} ${purity}% - ${fineWt}g fine`,
      date,
      timestamp: editData?.timestamp || new Date().toISOString()
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50" onClick={onClose}>
      <div 
        className="bg-white rounded-t-3xl w-full max-w-md p-6 space-y-4 animate-slide-up max-h-[85vh] overflow-y-auto" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-bold text-gray-800">✨ Fine Metal Deposit</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="bg-blue-50 p-3 rounded-lg text-xs text-blue-800">
          <strong>Note:</strong> This adds metal credit (grams) to the order, not ₹ payment.
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">Type</label>
            <div className="grid grid-cols-2 gap-2">
              {['Cadbury', '995', '999', 'Other'].map(type => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setMetalType(type)}
                  className={`py-2 rounded-lg text-xs font-semibold ${
                    metalType === type
                      ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Purity %</label>
              <input
                type="number"
                step="0.01"
                className="input-field w-full"
                value={purity}
                onChange={(e) => setPurity(e.target.value)}
                placeholder="e.g., 99.9"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Net Wt (g)</label>
              <input
                type="number"
                step="0.001"
                className="input-field w-full"
                value={netWt}
                onChange={(e) => setNetWt(e.target.value)}
                placeholder="e.g., 10.500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Fine Wt (g)</label>
            <input
              type="number"
              step="0.001"
              className="input-field w-full bg-green-50 font-semibold text-green-700"
              value={fineWt}
              onChange={(e) => setFineWt(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Reference/Note (optional)</label>
            <input
              type="text"
              className="input-field w-full"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Reference number or note"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Date</label>
            <input
              type="date"
              className="input-field w-full"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!netWt || !purity || !fineWt}
            className="flex-1 py-3 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-lg font-semibold disabled:opacity-50"
          >
            {editData ? 'Update' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};
